/**
 * ProjectChip — the order's project as a chip, on the order's header and
 * Details (each labels it "Project"). There is no project page yet (projects
 * come with a later wave), so the chip opens a small popover of the project's
 * orders the caller may see, each a link chip, with an ⓘ saying so.
 */
import { ChevronDown } from "lucide-react";
import type { OrderProject } from "@shared-core/data/order-to-invoice-types";
import { paths } from "@shared-core/routing/routes";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import { InfoTip } from "../../patterns/InfoTip";
import { ObjectChip } from "../../patterns/ObjectChip";

export function ProjectChip({
  project,
  currentOrderNo,
}: {
  project: OrderProject;
  /** The order the chip is on: marked, not linked, in the list. */
  currentOrderNo: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Project ${project.project_no}, ${project.title}: its orders`}
          className="inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs font-medium outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="truncate">
            {project.project_no}
            <span className="text-muted-foreground"> · {project.title}</span>
          </span>
          <ChevronDown aria-hidden className="size-3 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2">
        <p className="flex items-center gap-1 text-sm font-semibold">
          <span>
            {project.project_no}
            <span className="font-normal text-muted-foreground">
              {" "}
              · {project.title}
            </span>
          </span>
          <InfoTip label="About projects">
            Project pages are coming later. Until then, a project groups the
            payer's orders under one number and title.
          </InfoTip>
        </p>
        <ul
          aria-label={`Orders in ${project.project_no}`}
          className="space-y-1.5"
        >
          {project.orders.map((o) => (
            <li key={o.order_no} className="flex items-start gap-2 text-sm">
              {o.order_no === currentOrderNo ? (
                <ObjectChip label={o.order_no} detail="this order" />
              ) : (
                <ObjectChip
                  label={o.order_no}
                  to={paths.orderDetail({ orderNo: o.order_no })}
                />
              )}
              <span className="min-w-0 text-muted-foreground">{o.title}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
